import type { ComponentTemplate } from "@v1/types/component-template";
import type { ComponentTemplateIndustryId } from "schemas/componentTemplates";
import type { ReploElementType } from "schemas/generated/element";
import type { TemplateDevices } from "./ChooseTemplateOverlay";

import React from "react";

import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import SplitButton from "@v1/components/designSystem/button/SplitButton";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import { useCreateElement } from "@v1/hooks/element/useCreateElement";
import { setEditorMode } from "@v1/reducers/core-reducer";
import { useEditorDispatch } from "@v1/store/store";
import { componentTemplatesRouter } from "@v1/trpc/routes/componentTemplates";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  AlignVerticalSpaceAround,
  ArrowLeft,
  File,
  Newspaper,
  ShoppingBag,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { getComponentTemplateIndustryName } from "replo-runtime/shared/componentTemplates";

import { ComponentTemplateSnapshot } from "../component-template/ComponentTemplateSnapshot";
import CopyTemplateLinkButton from "./CopyTemplateLinkButton";
import PreviewPlatformToggleGroup from "./PreviewPlatformToggleGroup";

interface TemplatePageProps {
  template: ComponentTemplate;
  setSubpageTemplate: (template: ComponentTemplate | null) => void;
  previewPlatform: TemplateDevices;
  setPreviewPlatform: (platform: TemplateDevices) => void;
  defaultElementType: ReploElementType;
}

const TemplatePage: React.FC<TemplatePageProps> = ({
  template,
  setSubpageTemplate,
  previewPlatform,
  setPreviewPlatform,
  defaultElementType,
}) => {
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get("folderId") || undefined;
  const logEvent = useLogAnalytics();
  const [duplicatePopoverOpen, setDuplicatePopoverOpen] = React.useState(false);

  const { data: componentTemplate, isLoading } =
    componentTemplatesRouter.get.useQuery({
      templateId: template.id,
    });

  const dispatch = useEditorDispatch();
  const { handleCreateElement } = useCreateElement();

  const handleBuildNoAi = async (type?: ReploElementType) => {
    const elementTypeToUse = type ?? defaultElementType;
    logEvent("editor.componentTemplate.used", {
      componentTemplateId: template.id,
      collectionId: template.collectionId,
      categoryId: template.categoryId,
      componentTemplateName: template.name,
      componentTemplateType: template.type,
      componentScope: template.scope,
      source: "marketplace",
    });

    await handleCreateElement({
      type: elementTypeToUse,
      initialTemplate: componentTemplate!,
      folderId,
    });

    dispatch(setEditorMode("edit"));
    setDuplicatePopoverOpen(false);
  };

  const industryName = template.industryId
    ? getComponentTemplateIndustryName(
        template.industryId as ComponentTemplateIndustryId,
      )
    : "No Industry";

  return (
    <div className="mx-12 mt-12 flex h-full w-full flex-col items-center gap-12">
      <div className="flex w-full max-w-[1440px] gap-10 rounded-xl bg-white p-10">
        <div className="relative min-w-[400px] flex-1 overflow-hidden">
          <div className="absolute left-4 top-4 z-20 items-center">
            <DeprecatedIconButton
              variant="secondary"
              size="base"
              icon={<ArrowLeft size={20} />}
              aria-label="Back"
              onClick={() => setSubpageTemplate(null)}
              UNSAFE_className="shadow-md"
            />
          </div>
          <div className="absolute right-4 top-4 z-20 items-center">
            <PreviewPlatformToggleGroup
              previewPlatform={previewPlatform}
              setPreviewPlatform={setPreviewPlatform}
            />
          </div>

          {previewPlatform === "mobile" ? (
            <div className="relative mt-4 flex items-center justify-center overflow-hidden">
              <div className="pointer-events-none absolute top-0 z-10 flex min-h-[848px] w-full min-w-[417px] justify-center">
                <img src="/images/templates/Iphone 14.svg" alt="iPhone frame" />
              </div>

              <div className="overflow-hidden rounded-[84px]">
                <div className="no-scrollbar max-h-[848px] w-[400px] overflow-auto">
                  <ComponentTemplateSnapshot
                    componentTemplate={template}
                    previewPlatform={previewPlatform}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="no-scrollbar max-h-[100vh] overflow-auto">
              <div className="overflow-hidden rounded-xl">
                <ComponentTemplateSnapshot
                  componentTemplate={template}
                  previewPlatform={previewPlatform}
                />
              </div>
              {/* Spacing to account for margin at top of screen */}
              <div className="h-36" />
            </div>
          )}
        </div>
        <div className="flex min-w-[480px] flex-1 flex-col gap-10">
          <div className="flex justify-between">
            <div className="flex max-w-[50%] flex-col items-start gap-2">
              {template.logoUrl && (
                <img
                  src={template.logoUrl}
                  alt={`${template.name} logo`}
                  className="max-h-8 object-contain"
                />
              )}
              <div className="header-h3 truncate">{template.name}</div>
              {industryName && (
                <div className="text-muted text-base">{industryName}</div>
              )}
            </div>
            <div>
              <CopyTemplateLinkButton template={template} />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <DeprecatedButton
              variant="tertiary"
              size="base"
              onClick={() => setSubpageTemplate(null)}
            >
              Back
            </DeprecatedButton>
            <SplitButton
              buttonText="Duplicate Template"
              buttonId="duplicate-template"
              size="base"
              variant="primary"
              onButtonClick={() => void handleBuildNoAi()}
              isLoading={isLoading}
              disabled={isLoading}
              popoverProps={{
                isOpen: duplicatePopoverOpen,
                onOpenChange: (isOpen: boolean) => {
                  return setDuplicatePopoverOpen(isOpen);
                },
                content: (
                  <div className="flex flex-col gap-1 min-w-0">
                    <MenuItem
                      variant="default"
                      size="sm"
                      startEnhancer={<File size={12} />}
                      onClick={() => {
                        void handleBuildNoAi("page");
                      }}
                    >
                      Page
                    </MenuItem>
                    <MenuItem
                      variant="default"
                      size="sm"
                      startEnhancer={<ShoppingBag size={12} />}
                      onClick={() => {
                        void handleBuildNoAi("shopifyProductTemplate");
                      }}
                    >
                      Product Template
                    </MenuItem>
                    <MenuItem
                      variant="default"
                      size="sm"
                      startEnhancer={<Newspaper size={12} />}
                      onClick={() => {
                        void handleBuildNoAi("shopifyArticle");
                      }}
                    >
                      Blog Post
                    </MenuItem>
                    <MenuItem
                      variant="default"
                      size="sm"
                      startEnhancer={<AlignVerticalSpaceAround size={12} />}
                      onClick={() => {
                        void handleBuildNoAi("shopifySection");
                      }}
                    >
                      Section
                    </MenuItem>
                  </div>
                ),
                className: "w-[180px] p-1.5",
                skipTriggerStyles: true,
                hideCloseButton: true,
                sideOffset: 4,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePage;
